import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Quote } from "lucide-react";
import { Reveal, Eyebrow, Button } from "../components/primitives";
import { Seo } from "../components/Seo";
import { IMAGES } from "../lib/api";

const marqueeWords = ["Integrity", "Compassion", "Excellence", "Transparency", "Community", "Growth", "Stewardship", "Dignity"];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Eyebrow>A values-driven beauty company</Eyebrow>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif mt-6 text-5xl sm:text-6xl lg:text-[5rem] leading-[0.98] tracking-tight text-ink"
          >
            Beauty can transform more than appearance.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 text-lg leading-relaxed text-ink-soft max-w-xl"
          >
            We're building a business founded on values before profit — starting as a mobile
            salon, designed to become something far greater.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Button as={Link} to="/book" data-testid="hero-book-btn">
              Book Appointment <ArrowRight size={16} />
            </Button>
            <Button as={Link} to="/support" variant="outline" data-testid="hero-support-btn">
              Support the Vision
            </Button>
            <Button as={Link} to="/about" variant="ghost" data-testid="hero-story-btn">
              Learn Our Story
            </Button>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative"
        >
          <div className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-black/10">
            <motion.img
              style={{ y }}
              src={IMAGES.heroPortrait}
              alt="A person smiling warmly"
              className="w-full h-[420px] sm:h-[560px] object-cover object-[center_25%] scale-110"
            />
          </div>
          <div className="hidden sm:block absolute -bottom-6 -left-6 bg-cream border border-line rounded-2xl px-6 py-5 shadow-xl shadow-black/5">
            <p className="eyebrow">Est. 2026</p>
            <p className="font-serif text-2xl text-ink mt-1">Not just another salon.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Marquee = () => (
  <section className="py-10 border-y border-line bg-surface overflow-hidden" data-testid="values-marquee">
    <div className="flex whitespace-nowrap animate-marquee">
      {[...marqueeWords, ...marqueeWords].map((w, i) => (
        <span key={`${w}-${i}`} className="font-serif text-3xl text-ink/70 mx-10 flex items-center gap-10">
          {w} <span className="text-gold text-xl">✦</span>
        </span>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Values Before Profit"
        description="Glam Wagon is a values-driven mobile beauty company proving that integrity and profit can rise together. Book a service or support the vision."
        image={IMAGES.heroPortrait}
        schema={{
          "@context": "https://schema.org",
          "@type": "HealthAndBeautyBusiness",
          name: "Glam Wagon",
          description: "A values-driven mobile beauty company.",
          email: "hello@glamwagon.co",
        }}
      />
      <Hero />
      <Marquee />

      {/* Statement */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 sm:py-32" data-testid="statement-section">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <Reveal>
              <Eyebrow>Why we exist</Eyebrow>
              <h2 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight text-ink">
                This isn't just another salon.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <Reveal delay={0.15}>
              <p className="text-xl leading-relaxed text-ink-soft">
                Most companies treat values as a marketing line. We treat them as a constitution —
                a binding standard for how we serve people. The chair becomes a place to be seen,
                the mirror a place to be encouraged, and every person who sits with us is worthy of
                genuine care.
              </p>
              <Link to="/manifesto" className="inline-flex items-center gap-2 mt-8 text-ink font-medium hover:gap-3 transition-all group" data-testid="read-manifesto-link">
                Read our manifesto <ArrowRight size={16} className="text-gold" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Image + About teaser */}
      <section className="bg-surface" data-testid="about-teaser">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <img src={IMAGES.about} alt="Genuine connection" className="rounded-3xl w-full h-[520px] object-cover shadow-xl shadow-black/5" />
          </Reveal>
          <Reveal delay={0.15}>
            <Eyebrow>Our story</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight text-ink">
              Beauty services are simply the beginning.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-ink-soft">
              Glam Wagon began with a conviction: that a business can be profitable and profoundly
              good at the same time. We start mobile, meeting people where they are — but the vision
              reaches far beyond a single chair.
            </p>
            <Button as={Link} to="/about" variant="outline" className="mt-8" data-testid="about-teaser-btn">
              Learn our story
            </Button>
          </Reveal>
        </div>
      </section>

      {/* Support CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 sm:py-32" data-testid="support-cta">
        <Reveal>
          <div className="rounded-3xl border border-line bg-cream p-10 lg:p-20 relative overflow-hidden">
            <Quote className="absolute -top-4 right-8 text-gold/20" size={160} />
            <div className="relative max-w-2xl">
              <Eyebrow>Support the vision</Eyebrow>
              <h2 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight text-ink">
                Help build something meaningful from the ground up.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-ink-soft">
                We're an early-stage startup. Early believers aren't giving to charity — they're
                investing in a better way of doing business.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button as={Link} to="/support" data-testid="home-support-btn">
                  Become an early believer
                </Button>
                <Button as={Link} to="/values" variant="ghost" data-testid="home-values-btn">
                  Explore our values <ArrowRight size={16} />
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
